import React, {useMemo} from "react";
import {
    Header,
    LoadingWrapper,
    PageSizeWrapper,
    StyledDocumentsListContent,
    StyledDocumentsListWrapper,
    StyledPageSizeLabel
} from "./DataSourceDocumentList.styled";
import {Button, CircularProgress, Pagination, Stack, Tooltip} from "@mui/material";
import {ReactComponent as PlusIcon} from "../../../assets/icons/users-plus.svg";
import BackButton from "../../../components/common/BackButton";
import DownloadIcon from '@mui/icons-material/Download';
import PreviewIcon from '@mui/icons-material/Preview';
import DeleteIcon from '@mui/icons-material/Delete';
import AddDocumentDialog from "../../../components/data-sources/AddDocumentDialog";
import {usePageTitle} from '../../../hooks/usePageTitle';
import {useAppConstants} from '../../../config/appConstants';
import ConfirmationDialog from "../../../components/dialogs/ConfirmationDialog";
import {useTranslation} from 'react-i18next';
import ResponsiveTable, {ResponsiveTableColumn} from "../../../components/common/ResponsiveTable";
import {
    ActionButton,
    ContextMenu,
    ContextMenuItem,
    OptionsButton
} from "../../../components/data-sources/DataSourceTable.styled";
import {ReactComponent as MoreIcon} from "../../../assets/icons/more.svg";
import {PaginationWrapper, StyledTableContainer} from "../../../components/common/TableContainer.styled";
import {DocumentDataResponse} from "../../../models/collections.models";
import {useDataSourceDocumentsList} from "./useDataSourceDocumentsList";
import PageSizeSelector from "../../../components/common/PageSizeSelector";
import SearchInput from "../../../components/common/SearchInput";


const DataSourceDocumentList = () => {
    const {t} = useTranslation();
    const {DATA_SOURCE_TITLE_PREFIX} = useAppConstants();

    const {
        hasEditPermission,
        documents,
        dataSourceName,
        documentsDataSourceId,
        loading,

        searchTerm,
        currentPage,
        totalPages,
        pageSize,
        pageSizeOptions,
        contextMenu,

        addDocumentDialog,
        setAddDocumentDialog,
        documentToDelete,
        isDeleteDialogOpen,
        setIsDeleteDialogOpen,

        confirmDeleteDocument,
        handleContextMenu,
        handleDocumentAdded,
        handleCloseContextMenu,
        handleMenuPreview,
        handleMenuDownload,
        handleMenuDelete,
        handleBackToSource,
        handlePageChange,
        handlePageSizeChange,
        handleSearchTermChange,
    } = useDataSourceDocumentsList();

    usePageTitle(dataSourceName ? `${DATA_SOURCE_TITLE_PREFIX}${dataSourceName}` : DATA_SOURCE_TITLE_PREFIX, false);

    const columns: ResponsiveTableColumn<DocumentDataResponse>[] = useMemo(() => [
        {
            id: 'name',
            header: t('dataSourceDocuments.name'),
            render: (d) => d.name,
        },
        {
            id: 'owner',
            header: t('dataSourceDocuments.owner'),
            render: (d) => d.owner?.email || t('dataSourceDocuments.noOwner'),
        },
        {
            id: 'size',
            header: t('dataSourceDocuments.size'),
            render: (d) => `${(d.size).toFixed(2)} MB`,
        },
        {
            id: 'extension',
            header: t('dataSourceDocuments.extension'),
            render: (d) => d.extension,
        },
        {
            id: 'createdAt',
            header: t('dataSourceDocuments.createdAt'),
            render: (d) => new Date(d.createdAt).toLocaleDateString(),
        },
        {
            id: 'actions',
            header: '',
            headerLabel: t('common.actions') as string,
            cellClassName: 'actions-cell',
            render: (d) => (
                <>
                    <Tooltip title={t('common.actions')}>
                        <ActionButton
                            className="mobile-only"
                            variant="outlined"
                            aria-haspopup="menu"
                            aria-controls={contextMenu?.document.id === d.id ? `ds-documents-menu-${d.id}` : undefined}
                            aria-expanded={contextMenu?.document.id === d.id ? true : undefined}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                e.stopPropagation();
                                handleContextMenu(e, d);
                            }}
                        >
                            {t('common.actions')}
                        </ActionButton>
                    </Tooltip>
                    <Tooltip title={t('common.actions')}>
                        <OptionsButton
                            className="desktop-only"
                            size="small"
                            aria-label={t('common.actions') as string}
                            aria-haspopup="menu"
                            aria-controls={contextMenu?.document.id === d.id ? `ds-documents-menu-${d.id}` : undefined}
                            aria-expanded={contextMenu?.document.id === d.id ? true : undefined}
                            style={{ width: 26 }}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                e.stopPropagation();
                                handleContextMenu(e, d);
                            }}
                        >
                            <MoreIcon aria-hidden={true} focusable={false}/>
                        </OptionsButton>
                    </Tooltip>
                </>
            ),
        },
    ], [t, handleContextMenu]);

    return (
        <StyledTableContainer role="region" aria-labelledby="ds-documents-title">
            <Header>
                <div>
                    <BackButton onClick={handleBackToSource}/>
                </div>
                <div className="title-row">
                    <h2 id="ds-documents-title">{t('dataSourceDocuments.title')}: {dataSourceName || t('dataSourceDocuments.defaultName')}</h2>

                    <Button
                        disabled={!hasEditPermission}
                        startIcon={<PlusIcon aria-hidden={true} focusable={false}/>}
                        variant="contained"
                        color="primary"
                        onClick={() => setAddDocumentDialog(true)}
                        aria-label={t('dataSourceDocuments.addDocument') as string}
                    >
                        {t('dataSourceDocuments.addDocument')}
                    </Button>
                </div>
            </Header>

            <StyledDocumentsListContent aria-live="polite" aria-atomic={true} aria-busy={loading}>
                <Stack direction="row" alignItems="center" gap={2}>
                    <SearchInput onChange={handleSearchTermChange} value={searchTerm} />
                    <PageSizeWrapper>
                        <StyledPageSizeLabel>{t('dataSourceDocuments.pageSizeLabel')}:</StyledPageSizeLabel>
                        <PageSizeSelector
                            options={pageSizeOptions}
                            selectedSize={pageSize}
                            onChange={handlePageSizeChange}
                        />
                    </PageSizeWrapper>
                </Stack>

                {loading && (
                    <LoadingWrapper>
                        <CircularProgress />
                    </LoadingWrapper>
                )}

                {!loading && (
                    <StyledDocumentsListWrapper>
                        <ResponsiveTable
                            columns={columns}
                            data={documents}
                            getRowKey={(d) => d.id}
                            emptyState={t('dataSourceDocuments.noDocuments')}
                            ariaLabel={t('dataSourceDocuments.table.ariaLabel') as string}
                        />

                        <ContextMenu
                            id={contextMenu ? `ds-documents-menu-${contextMenu.document.id}` : undefined}
                            open={contextMenu !== null}
                            onClose={handleCloseContextMenu}
                            anchorEl={contextMenu?.anchorEl ?? null}
                            anchorReference={contextMenu?.anchorEl ? 'anchorEl' : 'anchorPosition'}
                            anchorPosition={
                                contextMenu && contextMenu.mouseY !== undefined && contextMenu.mouseX !== undefined
                                    ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                                    : undefined
                            }
                            MenuListProps={{
                                autoFocusItem: true,
                                'aria-labelledby': contextMenu ? `ds-documents-menu-${contextMenu.document.id}` : undefined,
                            }}
                        >
                            {contextMenu && [
                                (
                                    <ContextMenuItem key="preview" onClick={() => handleMenuPreview(contextMenu.document)}>
                                        <PreviewIcon aria-hidden={true} focusable={false} style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                                        {t('dataSourceDocuments.preview')}
                                    </ContextMenuItem>
                                ),
                                (
                                    <ContextMenuItem key="download" onClick={() => handleMenuDownload(contextMenu.document)}>
                                        <DownloadIcon aria-hidden={true} focusable={false} style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                                        {t('dataSourceDocuments.download')}
                                    </ContextMenuItem>
                                ),
                                // (contextMenu.document.conversationId ? (
                                //     <ContextMenuItem key="assistant" onClick={() => handleMenuAiAssistant(contextMenu.document)}>
                                //         <ChatIcon aria-hidden={true} focusable={false} style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                                //         {t('dataSourceDocuments.aiAssistant')}
                                //     </ContextMenuItem>
                                // ) : null),
                                (hasEditPermission &&
									<ContextMenuItem key="delete" onClick={() => handleMenuDelete(contextMenu.document)} style={{color: '#f44336'}}>
										<DeleteIcon aria-hidden={true} focusable={false}
													style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                                        {t('dataSourceDocuments.delete')}
									</ContextMenuItem>
                                )
                            ]}
                        </ContextMenu>
                    </StyledDocumentsListWrapper>
                )}

                {!loading && (
                    <PaginationWrapper>
                        <Pagination
                            count={totalPages}
                            page={currentPage + 1}
                            onChange={handlePageChange}
                            shape="rounded"
                            color="primary"
                            aria-label={t('adminDataSourceList.pagination.ariaLabel') as string}
                        />
                    </PaginationWrapper>
                )}
            </StyledDocumentsListContent>


            {addDocumentDialog && (
                <AddDocumentDialog
                    open={addDocumentDialog}
                    onClose={() => setAddDocumentDialog(false)}
                    dataSourceId={documentsDataSourceId}
                    onDocumentCreated={handleDocumentAdded}
                />)}
            {documentToDelete && hasEditPermission && (
                <ConfirmationDialog
                    open={isDeleteDialogOpen}
                    onClose={() => setIsDeleteDialogOpen(false)}
                    onConfirm={confirmDeleteDocument}
                    title={t('dataSourceDocuments.deleteConfirmTitle')}
                    message={<>{t('dataSourceDocuments.deleteConfirmMessage')} &ldquo;{documentToDelete.name}&rdquo; {t('dataSourceDocuments.deleteConfirmFromSource')}?</>}
                    confirmButtonText={t('dataSourceDocuments.deleteButton') as string}
                    cancelButtonText={t('dataSourceDocuments.cancelButton') as string}
                />
            )}
        </StyledTableContainer>
    );
};

export default DataSourceDocumentList;
